import { Button } from '@/components/ui/button';
import { Component, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

interface InnerProps {
  fallback: ReactNode;
  children: ReactNode;
}

/**
 * Minimal class error boundary. `<Routes>` (classic React Router) has no
 * `errorElement`, so a render crash in a route subtree would otherwise blank
 * the whole app (white screen). This contains it.
 */
class ErrorBoundaryInner extends Component<InnerProps, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: unknown) {
    // eslint-disable-next-line no-console
    console.error('[ProjectErrorBoundary] render crash', error, info);
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

/**
 * Wraps the project view. Catches render crashes — most importantly opening a
 * project that isn't available offline (its network data fails to load, a
 * downstream component renders with missing data and throws). Shows an
 * offline-aware message instead of a white screen. Keyed by `projectId` so
 * navigating to another project clears a previous error.
 */
export function ProjectErrorBoundary({ children }: { children: ReactNode }) {
  const { projectId } = useParams();
  const { t } = useTranslation('project');
  const navigate = useNavigate();
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;

  return (
    <ErrorBoundaryInner
      key={projectId}
      fallback={
        <div className='flex h-full w-full flex-col items-center justify-center gap-4 p-8 text-center'>
          <h2 className='text-lg font-medium text-slate-800 dark:text-stone-100'>
            {offline ? t('errorBoundary.offlineTitle') : t('errorBoundary.title')}
          </h2>
          <p className='max-w-md text-sm text-slate-500 dark:text-stone-400'>
            {offline ? t('errorBoundary.offlineMessage') : t('errorBoundary.message')}
          </p>
          <div className='flex gap-2'>
            <Button variant='outline' onClick={() => navigate('/')}>
              {t('errorBoundary.backHome')}
            </Button>
            <Button onClick={() => window.location.reload()}>{t('errorBoundary.reload')}</Button>
          </div>
        </div>
      }>
      {children}
    </ErrorBoundaryInner>
  );
}
